/**
 * React Bits — AnimatedContent, adapted from
 * https://reactbits.dev/animations/animated-content
 *
 * Fade-and-rise on scroll into view. Snaps in immediately if reduced-motion.
 */

import { motion, useInView } from 'motion/react';
import { useRef, type ReactNode } from 'react';

interface AnimatedContentProps {
  children: ReactNode;
  distance?: number;
  duration?: number;
  delay?: number;
  className?: string;
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function AnimatedContent({
  children,
  distance = 16,
  duration = 0.45,
  delay = 0,
  className,
}: AnimatedContentProps) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '0px 0px -40px 0px' });
  const reduce = prefersReducedMotion();

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: distance }}
      animate={inView || reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: distance }}
      transition={{ duration: reduce ? 0 : duration, delay: reduce ? 0 : delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

export default AnimatedContent;
